import { useState, useEffect, useRef, useCallback } from "react";

interface UseMapResizingProps {
  minHeight: number;
  maxHeight: number;
  setHeight: (height: string) => void;
}

const useMapResizing = ({
  minHeight,
  maxHeight,
  setHeight,
}: UseMapResizingProps) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const initialMouseY = useRef<number>(0);
  const initialHeight = useRef<number>(0);
  // Store final height to update the store only once when drag ends
  const finalHeight = useRef<string>("");
  // Cache the map container reference to avoid querying DOM repeatedly
  const mapContainerRef = useRef<HTMLElement | null>(null);
  const rafId = useRef<number>(0);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();

    // Store initial mouse position
    initialMouseY.current = e.clientY;

    // Get the current height of the map container
    const mapContainer = document.querySelector(
      '[data-testid="map-container"]',
    ) as HTMLElement;

    if (mapContainer) {
      mapContainerRef.current = mapContainer;
      initialHeight.current = mapContainer.offsetHeight;
      finalHeight.current = `${mapContainer.offsetHeight}px`;
    } else {
      // Fallback: calculate based on the current viewport and drag handle position
      const dragHandle = e.currentTarget as HTMLElement;
      const rect = dragHandle.getBoundingClientRect();
      // Estimate current map height based on handle position relative to viewport
      initialHeight.current = rect.top - 100; // Assuming navbar + some padding is ~100px
      finalHeight.current = `${initialHeight.current}px`;
    }

    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const onMouseMove = (e: MouseEvent) => {
      // Calculate the difference from initial position
      const deltaY = e.clientY - initialMouseY.current;
      let newHeight = initialHeight.current + deltaY;

      // Apply constraints
      if (newHeight < minHeight) {
        newHeight = minHeight;
      }

      if (newHeight > maxHeight) {
        newHeight = maxHeight;
      }

      const heightStr = `${newHeight}px`;
      finalHeight.current = heightStr;

      // Resize the container directly so the drag feels live, but at most once
      // per frame — the store (and the React re-render + Google Maps marker
      // overlay redraw it triggers) is still only updated on mouseup.
      if (mapContainerRef.current && !rafId.current) {
        rafId.current = requestAnimationFrame(() => {
          rafId.current = 0;
          if (mapContainerRef.current) {
            mapContainerRef.current.style.height = finalHeight.current;
          }
        });
      }
    };

    const onMouseUp = () => {
      cancelAnimationFrame(rafId.current);
      rafId.current = 0;

      // Update the store only once when drag ends
      // This also avoids forcing Google Maps to redraw marker overlays on every
      // mousemove while the user is dragging the resize handle.
      if (finalHeight.current) {
        setHeight(finalHeight.current);
      }

      setIsDragging(false);
      initialMouseY.current = 0;
      initialHeight.current = 0;
      mapContainerRef.current = null;
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      cancelAnimationFrame(rafId.current);
      rafId.current = 0;
    };
  }, [isDragging, minHeight, maxHeight, setHeight]);

  return { onMouseDown, isDragging };
};

export default useMapResizing;
